import React, { useCallback } from 'react';
import { useTheme } from 'styled-components';

import { useDisclosure } from 'src/hooks/useDisclosure';
import { adjustKycAndFeaturesConfig } from 'src/utils/adjust-kyc-and-features-config';

import { HeaderLeftPartWrapper } from './components/HeaderLeftPartWrapper';
import { HeaderRightPartWrapper } from './components/HeaderRightPartWrapper';
import { HeaderMobileIconsWrapper } from './components/HeaderMobileIconsWrapper';

import { LanguagesModal } from './modals/LanguagesModal';
import { LoggedUserModal } from './modals/LoggedUserModal';

import { HeaderProps, IconToExcludeType } from './Header.types';
import { HeaderContainer } from './Header.styles';

import { QuickSearchModal } from './modals/QuickSearchModal';
import { ThemeProvider } from 'src/packages/theme';

export const copyToClipboardDefault = async (value: string, message = 'Copied!') => {
  await navigator.clipboard.writeText(value);
  alert(message);
};

const Header: React.FC<HeaderProps> = ({
  hideMainNav,
  hideRightNav,
  excludeRightIcons,
  user,
  userLoading = false,
  logoTitle,
  quickSearchData,
  announcements,
  notifications,
  locale,
  assets,
  appTheme,
  logout,
  copyToClipboard,
  theme = 'dark',
  kycAndFeaturesConfig,
  topNoticeBarHeight,
}: HeaderProps) => {
  const contextTheme = useTheme();

  const {
    isOpen: isLanguagesModalOpen,
    close: closeLanguagesModal,
    open: openLanguagesModal,
  } = useDisclosure(false);
  const {
    isOpen: isLoggedUserModalOpen,
    close: closeLoggedUserModal,
    open: openLoggedUserModal,
  } = useDisclosure(false);
  const {
    isOpen: isQuickSearchModalOpen,
    close: closeQuickSearchModal,
    open: openQuickSearchModal,
  } = useDisclosure(false);

  const {
    tradeSpotPlatformEnabled,
    tradeDerivativesPlatformEnabled,
    convertPlatformEnabled,
    earnPlatformEnabled,
    p2pPlatformEnabled,
  } = adjustKycAndFeaturesConfig(kycAndFeaturesConfig);

  const shouldHideIcon = useCallback(
    (icon: IconToExcludeType) => {
      return !!(excludeRightIcons && excludeRightIcons.includes(icon));
    },
    [excludeRightIcons]
  );

  return (
    <ThemeProvider theme={theme} rtl={contextTheme.rtl}>
      <HeaderContainer>
        <LanguagesModal
          isOpen={isLanguagesModalOpen}
          onClose={closeLanguagesModal}
          locale={locale}
        />

        {!!user && (
          <LoggedUserModal
            isOpen={isLoggedUserModalOpen}
            onClose={closeLoggedUserModal}
            user={user}
            logout={logout}
            copyToClipboard={copyToClipboard}
            theme={theme}
          />
        )}

        {quickSearchData && (
          <QuickSearchModal
            isOpen={isQuickSearchModalOpen}
            onClose={closeQuickSearchModal}
            quickSearchData={quickSearchData}
            theme={theme}
            kycAndFeaturesConfig={kycAndFeaturesConfig}
          />
        )}

        <HeaderLeftPartWrapper
          logoTitle={logoTitle}
          hideMainNav={hideMainNav}
          convertPlatformEnabled={convertPlatformEnabled}
          p2pPlatformEnabled={p2pPlatformEnabled}
          tradeSpotPlatformEnabled={tradeSpotPlatformEnabled}
          tradeDerivativesPlatformEnabled={tradeDerivativesPlatformEnabled}
          earnPlatformEnabled={earnPlatformEnabled}
          theme={theme}
        />

        <HeaderRightPartWrapper
          quickSearchData={quickSearchData}
          theme={theme}
          kycAndFeaturesConfig={kycAndFeaturesConfig}
          user={user}
          userLoading={userLoading}
          hideRightNav={hideRightNav}
          assets={assets}
          logout={logout}
          copyToClipboard={copyToClipboard}
          shouldHideIcon={shouldHideIcon}
          notifications={notifications}
          announcements={announcements}
          locale={locale}
          appTheme={appTheme}
        />

        <HeaderMobileIconsWrapper
          quickSearchData={quickSearchData}
          user={user}
          userLoading={userLoading}
          p2pPlatformEnabled={p2pPlatformEnabled}
          earnPlatformEnabled={earnPlatformEnabled}
          tradeSpotPlatformEnabled={tradeSpotPlatformEnabled}
          tradeDerivativesPlatformEnabled={tradeDerivativesPlatformEnabled}
          convertPlatformEnabled={convertPlatformEnabled}
          notifications={notifications}
          openQuickSearchModal={openQuickSearchModal}
          openLoggedUserModal={openLoggedUserModal}
          openLanguagesModal={openLanguagesModal}
          topNoticeBarHeight={topNoticeBarHeight}
        />
      </HeaderContainer>
    </ThemeProvider>
  );
};

export default Header;
